import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
  fontSize: 13.5, color: "#111827", outline: "none",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };

function nowLocalDatetime() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function FuelLogFormModal({ vehicles, defaultVehicleId, onClose, onSubmit }) {
  const [vehicleId,   setVehicleId]   = useState(defaultVehicleId || "");
  const [filledAt,    setFilledAt]    = useState(nowLocalDatetime());
  const [odometerKm,  setOdometerKm]  = useState("");
  const [liters,      setLiters]      = useState("");
  const [cost,        setCost]        = useState("");
  const [fuelStation, setFuelStation] = useState("");
  const [saving,      setSaving]      = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!vehicleId) return toast.error("Select a vehicle");
    if (!filledAt) return toast.error("Fill-up date/time is required");
    setSaving(true);
    try {
      await onSubmit({
        vehicleId,
        filledAt:    new Date(filledAt).toISOString(),
        odometerKm:  odometerKm ? Number(odometerKm) : null,
        liters:      liters ? Number(liters) : null,
        cost:        cost ? Number(cost) : null,
        fuelStation: fuelStation.trim() || null,
      });
      toast.success("Fuel log added");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save fuel log");
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 440,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
          }}
        >
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Add Fuel Log</div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Vehicle *</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} value={vehicleId} onChange={e => setVehicleId(e.target.value)}>
                <option value="">— Select vehicle —</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.nickname || v.plateNumber}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Fill-up Date/Time *</label>
              <input type="datetime-local" style={inputStyle} value={filledAt} onChange={e => setFilledAt(e.target.value)} />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Odometer (km)</label>
                <input type="number" style={inputStyle} value={odometerKm} onChange={e => setOdometerKm(e.target.value)} placeholder="Optional" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Liters</label>
                <input type="number" step="0.01" style={inputStyle} value={liters} onChange={e => setLiters(e.target.value)} placeholder="Optional" />
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Cost (₹)</label>
                <input type="number" step="0.01" style={inputStyle} value={cost} onChange={e => setCost(e.target.value)} placeholder="Optional" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Fuel Station</label>
                <input style={inputStyle} value={fuelStation} onChange={e => setFuelStation(e.target.value)} placeholder="Optional" />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              style={{
                marginTop: 6, width: "100%", height: 42, borderRadius: 10, border: "none",
                background: saving ? "#9CA3AF" : "#4F46E5",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {saving ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : "Add Fuel Log"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
